import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, Video, Phone, MapPin, Euro, Clock } from "lucide-react";

const empty = { code: "", label: "", description: "", duration_minutes: 30, price_eur: 0, requires_payment: false, delivery: "visio", order: 99, active: true, color: "#06B6D4" };
const DELIVERY_ICON = { visio: Video, phone: Phone, physical: MapPin };
const DELIVERY_LABEL = { visio: "Visio", phone: "Téléphone", physical: "Sur site" };

export default function AdminAppointmentTypes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/appointment-types").then((r) => setItems(r.data)).catch(() => toast.error("Erreur"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code || !form.label) return toast.error("Code et libellé requis");
    try {
      const payload = { ...form, duration_minutes: parseInt(form.duration_minutes) || 30, price_eur: parseFloat(form.price_eur) || 0, order: parseInt(form.order) || 99 };
      if (editing) await api.patch(`/admin/appointment-types/${editing.id}`, payload);
      else await api.post("/admin/appointment-types", payload);
      setOpen(false); setEditing(null); setForm(empty); load();
      toast.success("Enregistré");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const edit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Supprimer ce type de RDV ?")) return; await api.delete(`/admin/appointment-types/${id}`); load(); };
  const toggle = async (id, active) => { await api.patch(`/admin/appointment-types/${id}`, { active }); load(); };

  return (
    <div data-testid="admin-appointment-types">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Types de rendez-vous</h1>
          <p className="text-sm text-[#64748B] mt-1">Définissez les formats proposés (gratuit / payant), durée, prix et mode de livraison. Visibles sur la page publique de prise de RDV et dans le portail client.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="appt-type-add" className="bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] font-bold"><Plus size={16} className="mr-1.5" />Nouveau type</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouveau"} type de RDV</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Code (slug unique) *</Label><Input data-testid="appt-type-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/\s+/g, "_") })} placeholder="ex: audit_strategique" disabled={!!editing} /></div>
                <div><Label>Libellé *</Label><Input data-testid="appt-type-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex : Audit stratégique 2h" /></div>
              </div>
              <div><Label>Description (affichée publiquement)</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Pour qui · contenu · ce qui est inclus…" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Durée (min)</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} /></div>
                <div><Label>Prix TTC (€)</Label><Input data-testid="appt-type-price" type="number" step="0.01" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value, requires_payment: parseFloat(e.target.value) > 0 })} /></div>
                <div><Label>Ordre d'affichage</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Mode</Label>
                  <select className="w-full h-9 border border-input rounded-md px-2 text-sm" value={form.delivery} onChange={(e) => setForm({ ...form, delivery: e.target.value })}>
                    <option value="visio">Visio</option><option value="phone">Téléphone</option><option value="physical">Sur site</option>
                  </select>
                </div>
                <div><Label>Couleur</Label><Input type="color" value={form.color || "#06B6D4"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 cursor-pointer" /></div>
              </div>
              <div className="bg-[#FAFAFA] border border-black/8 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <Label className="cursor-pointer">Paiement requis (Stripe)</Label>
                  <div className="text-xs text-[#64748B] mt-0.5">Activé automatiquement si le prix &gt; 0</div>
                </div>
                <Switch checked={form.requires_payment} onCheckedChange={(v) => setForm({ ...form, requires_payment: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button data-testid="appt-type-save" onClick={save} className="bg-[#0A1128] text-white">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-3 bg-white border border-black/8 rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="appt-types-table">
          <thead className="bg-[#FAFAFA] border-b border-black/8 text-[10px] font-bold tracking-widest uppercase text-[#64748B]">
            <tr>
              <th className="text-left px-3 py-2.5">Ordre</th>
              <th className="text-left px-3 py-2.5">Libellé / Code</th>
              <th className="text-center px-3 py-2.5">Mode</th>
              <th className="text-right px-3 py-2.5">Durée</th>
              <th className="text-right px-3 py-2.5">Prix TTC</th>
              <th className="text-center px-3 py-2.5">Paiement</th>
              <th className="text-center px-3 py-2.5">Actif</th>
              <th className="text-right px-3 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="text-center text-sm text-[#64748B] py-8">Aucun type de RDV. Cliquez "Nouveau type" pour démarrer.</td></tr>}
            {items.map((it) => {
              const Icon = DELIVERY_ICON[it.delivery] || Video;
              return (
                <tr key={it.id} data-testid={`appt-type-${it.id}`} className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]">
                  <td className="px-3 py-3"><span className="inline-block w-6 h-6 rounded-full text-center font-bold text-xs leading-6 text-white" style={{ background: it.color || "#06B6D4" }}>{it.order}</span></td>
                  <td className="px-3 py-3">
                    <button onClick={() => edit(it)} className="display font-extrabold text-[#0A1128] hover:underline text-left">{it.label}</button>
                    <div className="text-[10px] text-[#94a3b8] font-mono">{it.code}</div>
                    {it.description && <div className="text-[11px] text-[#64748B] mt-0.5 line-clamp-1 max-w-md">{it.description}</div>}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-black/5 text-[#0A1128]">
                      <Icon size={10} />{DELIVERY_LABEL[it.delivery]}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-xs"><Clock size={11} className="inline mr-1 text-[#64748B]" />{it.duration_minutes} min</td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    {it.price_eur > 0 ? (
                      <span className="font-extrabold text-[#0A1128]"><Euro size={11} className="inline mr-0.5 text-[#06B6D4]" />{Number(it.price_eur).toLocaleString("fr-FR", { minimumFractionDigits: 0 })}</span>
                    ) : <span className="text-green-600 font-bold text-xs">GRATUIT</span>}
                  </td>
                  <td className="px-3 py-3 text-center">{it.requires_payment ? <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-[#FFDCA8] text-[#92400E]">Stripe</span> : <span className="text-[10px] text-[#94a3b8]">—</span>}</td>
                  <td className="px-3 py-3 text-center"><Switch checked={it.active !== false} onCheckedChange={(v) => toggle(it.id, v)} data-testid={`appt-type-toggle-${it.id}`} /></td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => edit(it)} className="p-1.5 text-[#64748B] hover:text-[#06B6D4]" title="Modifier"><Edit2 size={14} /></button>
                      <button onClick={() => del(it.id)} className="p-1.5 text-[#94a3b8] hover:text-red-500" title="Supprimer"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
